import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

// Context
import { AuthProvider, useAuth } from "./context/AuthContext";

// Pages
import { LoginPage } from "./pages/LoginPage";
import { RegisterPage } from "./pages/RegisterPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { DemoPage } from "./pages/DemoPage";
import { AuthCallback } from "./pages/AuthCallback";
import { OnboardingPage } from "./pages/OnboardingPage";
import { SubscriptionPage } from "./pages/SubscriptionPage";
import { PaymentSuccessPage } from "./pages/PaymentSuccessPage";
import { DashboardPage } from "./pages/DashboardPage";
import { VentesPage } from "./pages/VentesPage";
import { DepensesPage } from "./pages/DepensesPage";
import { RecapCAPage } from "./pages/RecapCAPage";
import { SimulateurPage } from "./pages/SimulateurPage";
import { ParametresPage } from "./pages/ParametresPage";
import { HistoriquePage } from "./pages/HistoriquePage";
import { ContactPage } from "./pages/ContactPage";
import { AdminPage } from "./pages/AdminPage";
import { AdminMarketingPage } from "./pages/AdminMarketingPage";
import { PolitiqueConfidentialitePage } from "./pages/PolitiqueConfidentialitePage";
import { LandingPage } from "./pages/LandingPage";
import { MentionsLegalesCGUPage } from "./pages/MentionsLegalesCGUPage";
import { CGVPage } from "./pages/CGVPage";
import { StatutAutoEntrepreneurPage } from "./pages/StatutAutoEntrepreneurPage";
import { RetractationPage } from "./pages/RetractationPage";
import { CookieBanner } from "./components/layout/CookieBanner";
import { QontoCallbackPage } from "./pages/QontoCallbackPage";
import LandingV2Page from "./pages/LandingV2Page";

// Protected Route Component - vérifie aussi l'abonnement
const ProtectedRoute = ({ children, requireSubscription = true }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
    </div>
  );
  if (!user) return <Navigate to="/login" />;
  
  // Si l'utilisateur est abonné mais n'a pas complété son profil → onboarding obligatoire
  const needsOnboarding = user.subscription_status === "active" && !user.onboarding_complete;
  if (needsOnboarding && window.location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" />;
  }
  
  // Si l'abonnement est requis et pas actif, rediriger vers la page d'abonnement
  if (requireSubscription && user.subscription_status !== "active") {
    // Si onboarding pas fait, laisser passer vers onboarding
    if (!user.onboarding_complete) {
      return children;
    }
    return <Navigate to="/subscription" />;
  }
  
  return children;
};

// Route racine "/" :
//   - Si l'utilisateur est connecté → on l'envoie sur son dashboard
//   - Sinon → on renvoie sur la page de connexion (design + sobre que la landing)
const RootRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
    </div>
  );
  if (user) return <Navigate to="/dashboard" replace />;
  return <Navigate to="/login" replace />;
};

// App
function App() {
  return (
    <AuthProvider>
      <Toaster position="bottom-right" richColors />
      <BrowserRouter>
        <CookieBanner />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/demo" element={<DemoPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/onboarding" element={<ProtectedRoute requireSubscription={false}><OnboardingPage /></ProtectedRoute>} />
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/payment-success" element={<ProtectedRoute requireSubscription={false}><PaymentSuccessPage /></ProtectedRoute>} />
          <Route path="/ventes" element={<ProtectedRoute><VentesPage /></ProtectedRoute>} />
          <Route path="/depenses" element={<ProtectedRoute><DepensesPage /></ProtectedRoute>} />
          <Route path="/recap-ca" element={<ProtectedRoute><RecapCAPage /></ProtectedRoute>} />
          <Route path="/simulateur" element={<ProtectedRoute><SimulateurPage /></ProtectedRoute>} />
          <Route path="/historique" element={<ProtectedRoute><HistoriquePage /></ProtectedRoute>} />
          <Route path="/parametres" element={<ProtectedRoute><ParametresPage /></ProtectedRoute>} />
          <Route path="/contact" element={<ProtectedRoute><ContactPage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          <Route path="/admin/marketing" element={<ProtectedRoute><AdminMarketingPage /></ProtectedRoute>} />
          <Route path="/politique-confidentialite" element={<PolitiqueConfidentialitePage />} />
          <Route path="/confidentialite" element={<Navigate to="/politique-confidentialite" replace />} />
          <Route path="/mentions-legales-cgu" element={<MentionsLegalesCGUPage />} />
          <Route path="/mentions-legales" element={<Navigate to="/mentions-legales-cgu" replace />} />
          <Route path="/landing-v2" element={<LandingV2Page />} />
          <Route path="/cgu" element={<Navigate to="/mentions-legales-cgu" replace />} />
          <Route path="/cgv" element={<CGVPage />} />
          <Route path="/statut-auto-entrepreneur" element={<StatutAutoEntrepreneurPage />} />
          <Route path="/statut-ae" element={<Navigate to="/statut-auto-entrepreneur" replace />} />
          <Route path="/retractation" element={<RetractationPage />} />
          <Route path="/qonto/callback" element={<QontoCallbackPage />} />
          <Route path="/early-access" element={<LandingPage />} />
          <Route path="/" element={<RootRoute />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
